import styled from "styled-components";
import { PROJECT_THEME } from "../../theme";
import { Subtext } from "../../component/Subtext";
import { Section } from "../../component/Section";

const ROOT_MAX_WIDTH = '72rem';

const TitleAndSubtextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 2rem auto 0;
    max-width: ${ROOT_MAX_WIDTH};
`;

const HeroTitleRow = styled.div`
    margin-bottom: 0.5em;
`

const HeroTitle = styled.h1`
    display: inline;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`

const ContentColumn = styled.div`
    width: 100%;
    max-width: ${ROOT_MAX_WIDTH};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1.15rem;
`;

const Paragraph = styled.p`
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 1rem;
    line-height: 1.7;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    max-width: 68ch;
`;

const ValueGrid = styled.div`
    width: 100%;
    max-width: ${ROOT_MAX_WIDTH};
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;

    @media (max-width: 60rem) {
        grid-template-columns: 1fr;
    }
`;

const ValueCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.75rem;
    border: 1px solid ${({theme}) => theme.colour.LIGHT_GREY};
    border-radius: ${({theme}) => theme.BORDER_RADIUS};
    background: ${({theme}) => theme.colour.DARK_GREY};
`;

const ValueTitle = styled.h3`
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SERIF};
    font-weight: 700;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const ValueDescription = styled.p`
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.92rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
`;

const CTAWrapper = styled.div`
    width: 100%;
    max-width: ${ROOT_MAX_WIDTH};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
`;

const CTAButton = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    padding: 0.7rem 1.4rem;
    border: 1px solid ${({theme}) => theme.colour.COLOUR_LIGHT};
    border-radius: 4px;
    background: transparent;
    color: ${({theme}) => theme.colour.COLOUR_LIGHT};
    font-family: ${({theme}) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.82em;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    text-decoration: none;
    transition: color 140ms ease, border-color 140ms ease, transform 140ms ease;

    &:hover {
        color: ${({theme}) => theme.colour.OFF_WHITE};
        border-color: ${({theme}) => theme.colour.OFF_WHITE};
        transform: translateY(-1px);
    }
`;

const VALUES = [
    {
        title: 'Stack-agnostic',
        description: "We pick tools for the problem in front of us, not the stack we're most used to selling.",
    },
    {
        title: 'Senior, not layered',
        description: 'Every engagement is staffed by people who write the code themselves, not passed down through account layers.',
    },
    {
        title: 'Transparent delivery',
        description: 'Clear scope, regular reporting, and visibility into budget burn from the first week to the last.',
    },
    {
        title: 'Built to operate',
        description: 'We hand over systems your team can actually run, with documentation and knowledge transfer built in.',
    },
];

export default function About() {
    return (
        <>
            <TitleAndSubtextWrapper>
                <HeroTitleRow>
                    <HeroTitle>
                        About O&apos;Neill Software
                    </HeroTitle>
                </HeroTitleRow>
                <Subtext>
                    A small, stack-agnostic engineering practice built around outcomes
                </Subtext>
            </TitleAndSubtextWrapper>

            <Section title="story" color={PROJECT_THEME.colour.COLOUR_LIGHT} expand>
                <ContentColumn>
                    <Paragraph>
                        O&apos;Neill Software exists to solve hard engineering problems without the overhead
                        of a large consultancy. We work directly with founders and engineering leaders,
                        favour small senior teams over account structures, and choose whichever stack
                        actually fits the problem rather than the one we happen to sell.
                    </Paragraph>
                    <Paragraph>
                        That breadth spans product-focused web platforms, cloud and enterprise systems,
                        security engineering, graphics and GPU-accelerated tooling, embedded and systems
                        software, data engineering, and applied machine learning. The common thread is the
                        same regardless of stack: clear architecture, dependable delivery, and software
                        your team can maintain long after we hand it over.
                    </Paragraph>
                </ContentColumn>
            </Section>

            <Section title="values" color={PROJECT_THEME.colour.COLOUR_MID_LIGHT} expand>
                <ValueGrid>
                    {VALUES.map((value) => (
                        <ValueCard key={value.title}>
                            <ValueTitle>{value.title}</ValueTitle>
                            <ValueDescription>{value.description}</ValueDescription>
                        </ValueCard>
                    ))}
                </ValueGrid>
            </Section>

            <Section headless expand>
                <CTAWrapper>
                    <Paragraph>
                        Want to work together?
                    </Paragraph>
                    <CTAButton href="mailto:engineering@oneillsoft.com">
                        Get in touch
                    </CTAButton>
                </CTAWrapper>
            </Section>
        </>
    );
}
